import { useEffect, useState, useMemo } from "react";
import { format, parseISO } from "date-fns";
import {
  History,
  ArrowRight,
  Sparkles,
  UserMinus,
  Printer,
  ChevronDown,
  ChevronUp,
  Users,
  AlertTriangle,
} from "lucide-react";
import { Days } from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATE_META = {
  new_admit: {
    label: "New admit",
    color: "#d96c4e",
    bg: "#fbebe7",
    Icon: Sparkles,
    testId: "new-admit",
  },
  moved: {
    label: "Reassigned",
    color: "#6b4f8e",
    bg: "#f0ebf7",
    Icon: ArrowRight,
    testId: "moved",
  },
  unchanged: {
    label: "Continuity",
    color: "#0284c7",
    bg: "#e9f2ee",
    Icon: Users,
    testId: "unchanged",
  },
  discharged: {
    label: "Discharged",
    color: "#909590",
    bg: "#f1f5f9",
    Icon: UserMinus,
    testId: "discharged",
  },
};

function StateBadge({ state }) {
  const meta = STATE_META[state] || STATE_META.unchanged;
  const { Icon } = meta;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest"
      style={{ backgroundColor: meta.bg, color: meta.color }}
      data-testid={`diff-state-${meta.testId}`}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

function tryFormatDate(iso) {
  try {
    return format(parseISO(iso + "T00:00:00"), "EEE, MMM d");
  } catch {
    return iso;
  }
}

export default function ContinuityDiff({ date }) {
  const [open, setOpen] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await Days.diff(date);
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [date]);

  const handlePrint = () => {
    document.body.classList.add("print-diff-only");
    const cleanup = () => {
      document.body.classList.remove("print-diff-only");
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    setTimeout(() => window.print(), 60);
  };

  const displayDate = useMemo(() => tryFormatDate(date), [date]);
  const prevDisplay = useMemo(
    () => (data?.prev_date ? tryFormatDate(data.prev_date) : null),
    [data]
  );

  if (loading) {
    return (
      <div
        className="bg-white border border-[#e2e8f0] rounded-xl p-6 text-sm text-[#475569] no-print"
        data-testid="continuity-diff-loading"
      >
        Loading continuity diff…
      </div>
    );
  }
  if (!data) return null;

  const noPrev = !data.prev_date;
  const rows = data.rows || [];
  const counts = data.counts || {};

  return (
    <section
      className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.02)] scheduler-only"
      data-testid="continuity-diff-panel"
      data-print-scope="diff"
    >
      <header className="p-5 border-b border-[#e2e8f0] bg-[#f8fafc] flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#e9f2ee] text-[#0284c7] flex items-center justify-center">
            <History className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
              Scheduler view
            </p>
            <h3 className="font-heading text-lg font-semibold text-[#0f172a] leading-tight">
              Yesterday&apos;s owner vs today
            </h3>
            <p className="text-xs text-[#475569] mt-0.5">
              {noPrev ? (
                "No prior saved day yet — every patient is a new admit."
              ) : (
                <>
                  Comparing <span className="font-medium text-[#0f172a]">{prevDisplay}</span>
                  {" → "}
                  <span className="font-medium text-[#0f172a]">{displayDate}</span>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 no-print">
          <Button
            variant="outline"
            size="sm"
            className="border-[#e2e8f0] hover:bg-[#f1f5f9]"
            onClick={handlePrint}
            data-testid="print-diff-button"
          >
            <Printer className="w-4 h-4 mr-1.5" /> Print this diff
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-[#475569] hover:bg-[#f1f5f9]"
            onClick={() => setOpen((v) => !v)}
            data-testid="diff-toggle-button"
          >
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </header>

      {/* Print-only heading */}
      <div className="print-only px-5 pt-4">
        <h1 className="font-heading font-bold text-2xl">
          Continuity Diff — {displayDate}
        </h1>
        {prevDisplay && (
          <p className="text-sm text-[#475569]">Compared with {prevDisplay}</p>
        )}
      </div>

      {/* Summary chips */}
      <div className="px-5 py-3 flex flex-wrap gap-2 border-b border-[#e2e8f0] bg-white">
        {["new_admit", "moved", "unchanged", "discharged"].map((k) => {
          const meta = STATE_META[k];
          const c = counts[k] || 0;
          return (
            <span
              key={k}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
              style={{
                backgroundColor: c > 0 ? meta.bg : "#ffffff",
                color: meta.color,
                borderColor: c > 0 ? meta.color + "30" : "#e2e8f0",
              }}
              data-testid={`diff-count-${meta.testId}`}
            >
              <meta.Icon className="w-3.5 h-3.5" />
              <span className="font-mono">{c}</span> {meta.label}
            </span>
          );
        })}
      </div>

      {/* Discharge callout — highlighted section on top */}
      {counts.discharged > 0 && (
        <div
          className="mx-5 mt-4 rounded-lg border border-[#b94a48]/25 bg-[#fbebe7] p-4"
          data-testid="discharge-callout"
          data-print-scope="diff"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#b94a48] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <h4 className="font-heading font-semibold text-[#b94a48] text-sm">
                {counts.discharged} patient{counts.discharged !== 1 ? "s" : ""} discharged since {prevDisplay}
              </h4>
              <p className="text-[11px] text-[#8a3936] mt-0.5">
                These patients were on the previous day&apos;s list but are not in today&apos;s census. They are excluded from today&apos;s assignments.
              </p>
              <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1 text-sm">
                {rows.filter((r) => r.state === "discharged").map((r, i) => (
                  <li key={r.name + i} className="text-[#0f172a] flex items-center gap-2" data-testid={`discharge-row-${i}`}>
                    <span className="line-through text-[#909590]">{r.name}</span>
                    {r.unit && (
                      <span className="text-[10px] font-mono px-1 py-0 bg-white border border-[#f6d6ce] rounded text-[#8a3936]">
                        U{r.unit}
                      </span>
                    )}
                    {r.prev_provider_name && (
                      <span className="text-[10px] text-[#475569]">← {r.prev_provider_name}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="overflow-x-auto" data-testid="continuity-diff-body">
          {rows.length === 0 ? (
            <p className="px-6 py-8 text-sm text-[#475569] italic">
              No changes to show.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#f8fafc]">
                <tr className="text-left border-b border-[#e2e8f0]">
                  <th className="px-5 py-2 font-medium text-[#475569] text-xs uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-3 py-2 font-medium text-[#475569] text-xs uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="px-3 py-2 font-medium text-[#475569] text-xs uppercase tracking-wider">
                    Admission
                  </th>
                  <th className="px-3 py-2 font-medium text-[#475569] text-xs uppercase tracking-wider">
                    Yesterday
                  </th>
                  <th className="px-3 py-2 font-medium text-[#475569] text-xs uppercase tracking-wider">
                    Today
                  </th>
                  <th className="px-3 py-2 font-medium text-[#475569] text-xs uppercase tracking-wider">
                    Change
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const strike = r.state === "discharged";
                  return (
                    <tr
                      key={r.name + i}
                      className="border-b border-[#f1f5f9] hover:bg-[#f8fafc]"
                      data-testid={`diff-row-${r.state}-${i}`}
                    >
                      <td
                        className={[
                          "px-5 py-2 font-medium",
                          strike ? "line-through text-[#909590]" : "text-[#0f172a]",
                        ].join(" ")}
                      >
                        {r.name}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-[#545b58]">
                        {r.unit && (
                          <span className="px-1.5 py-0.5 bg-[#f1f5f9] rounded">
                            {r.unit}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-[#545b58]">
                        {r.admission_date}
                      </td>
                      <td
                        className={[
                          "px-3 py-2",
                          r.prev_provider_name
                            ? "text-[#0f172a]"
                            : "text-[#c4c4c0] italic",
                        ].join(" ")}
                      >
                        {r.prev_provider_name || "—"}
                      </td>
                      <td
                        className={[
                          "px-3 py-2",
                          r.today_provider_name
                            ? "text-[#0f172a] font-medium"
                            : "text-[#c4c4c0] italic",
                        ].join(" ")}
                      >
                        {r.today_provider_name || (r.state === "discharged" ? "—" : "Unassigned")}
                      </td>
                      <td className="px-3 py-2">
                        <StateBadge state={r.state} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
